import React from "react";
import {
  FileText,
  ArrowLeftRight,
  Link2,
  Server,
  ShieldCheck,
  RotateCcw,
  Search,
  Filter,
} from "lucide-react";

/**
 * Nested content for the "Filter by" FilterButton, modeled on TeamDropdownContent.
 *
 * Every section is single-select (the audit endpoint takes one value per field),
 * so each list uses radios and keeps its "All …" option as that field's reset.
 * Selections are staged in `draft` and only hit the table on Apply Filters.
 */
const MENU = [
  { key: "fileTypeLabel", label: "File Type",  icon: <FileText       className="w-4 h-4 text-[#223688]" /> },
  { key: "flowTypeLabel", label: "Flow Type",  icon: <ArrowLeftRight className="w-4 h-4 text-[#223688]" /> },
  { key: "refTypeLabel",  label: "Ref. Type",  icon: <Link2          className="w-4 h-4 text-[#223688]" /> },
  { key: "sourceLabel",   label: "Source",     icon: <Server         className="w-4 h-4 text-[#223688]" /> },
  { key: "statusLabel",   label: "Processed",  icon: <ShieldCheck    className="w-4 h-4 text-[#223688]" /> },
];

const TransactionDropdownContent = ({
  draft,
  setDraft,
  options = {},
  onApply,
  // Supplied by FilterButton so the popup can dismiss itself.
  onRequestClose,
}) => {
  // 🧩 Determine if filters are empty — every section still on its "All …" option
  const isFiltersEmpty =
    MENU.every(({ key }) => String(draft[key] ?? "").startsWith("All")) &&
    !String(draft.keyword ?? "").trim();

  // 🔹 Commit the staged selections. Every way of applying is terminal for the popup
  // — the Apply button, the search icon, and Enter in the search box all land here
  // and dismiss it afterwards.
  const handleApply = () => {
    onApply(draft);
    if (onRequestClose) onRequestClose();
  };

  // 🔹 Reset all selections — the parent clears applied state and re-seeds the draft.
  // Stays open so the user can pick a new filter set straight away.
  const handleReset = () => {
    if (onApply) onApply(null);
  };

  // 🔹 Select one option within a section
  const selectValue = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  // 🔹 Handle search term
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, keyword: value }));
  };

  return (
    <div className="bg-gray-50 dark:bg-acs-dark-bg p-fluid-3 rounded-xl border border-gray-100 dark:border-acs-dark-border shadow-md w-[800px] max-h-[70vh] overflow-y-auto">
      {/* === Search Row + Buttons === */}
      <div className="flex justify-between items-center mb-fluid-3">
        {/* === Left side: Search bar === */}
        <div className="relative flex items-center border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface rounded-full px-3 py-1.5 w-[clamp(14rem,30vw,20rem)] shadow-sm">
          <input
            type="text"
            placeholder="Search transaction no., reference..."
            value={draft.keyword ?? ""}
            onChange={handleSearchChange}
            onKeyDown={(e) => e.key === "Enter" && handleApply()}
            className="w-full bg-transparent outline-none text-fluid-sm text-gray-800 dark:text-acs-dark-text"
          />
          <button
            onClick={handleApply}
            className="absolute right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <Search size={18} strokeWidth={2} />
          </button>
        </div>

        {/* === Right side: Apply + Reset buttons === */}
        <div className="flex items-center gap-2">
          {/* Apply Filters */}
          <button
            onClick={handleApply}
            disabled={isFiltersEmpty}
            className={`flex items-center gap-1 text-sm font-medium rounded-md px-4 py-1.5 shadow-sm transition
              ${
                isFiltersEmpty
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#223688] text-white hover:bg-[#1a2c73] cursor-pointer"
              }`}
          >
            <Filter className="w-4 h-4" strokeWidth={2} />
            Apply Filters
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            disabled={isFiltersEmpty}
            className={`flex items-center gap-1 text-sm font-medium border px-4 py-1.5 rounded-md transition
              ${
                isFiltersEmpty
                  ? "text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50"
                  : "text-[#223688] hover:text-[#1a2c73] border-gray-300 hover:border-[#223688]"
              }`}
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* === Grid Layout for Filter Sections === */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-fluid-3">
        {MENU.map((section) => (
          <div
            key={section.key}
            className="bg-white dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-lg p-fluid-2 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-fluid-2">
              {section.icon}
              <h3 className="text-fluid-base font-semibold text-gray-800 dark:text-acs-dark-text">
                {section.label}
              </h3>
            </div>

            <div className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto pr-1">
              {(options[section.key] ?? []).map((opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-[#2A2D35] rounded-md px-2 py-1 cursor-pointer transition"
                >
                  <input
                    type="radio"
                    checked={draft[section.key] === opt}
                    onChange={() => selectValue(section.key, opt)}
                    name={section.key}
                    className="accent-[#223688] w-4 h-4 rounded-md"
                  />
                  <span className="truncate">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionDropdownContent;
